import type {
  BloomieTamSeniority,
  CdiJobType,
  FreelanceJobType,
  IJobProperties,
  JobType,
  CRAStatus,
  PaymentTerms,
} from './domain';

/** Codes d’erreur des lignes sentinelle (consultation `getMonthlyTamPrime` uniquement). */
export const PRIME_LINE_ERROR_CODES = {
  MISSION_NOT_FOUND: 'MISSION_NOT_FOUND',
  ASSEMBLY_ERROR: 'ASSEMBLY_ERROR',
  MISSING_PRICING: 'MISSING_PRICING',
  BLOOMER_NOT_FOUND: 'BLOOMER_NOT_FOUND',
  BILLING_ASSEMBLY_ERROR: 'BILLING_ASSEMBLY_ERROR',
} as const;

export type PrimeLineErrorCode = (typeof PRIME_LINE_ERROR_CODES)[keyof typeof PRIME_LINE_ERROR_CODES];

export type PrimeSplitRoles = Required<Pick<IJobProperties, 'passeur' | 'buteur' | 'capitaine' | 'defenseur'>>;

export type BasePrime<J extends JobType = JobType> = {
  jobFunnelProjectId: string;
  airtableId: string;
  clientSocietyName: string;
  bloomerFullName: string;
  jobType: J;
  monthlyMargin: number;
  monthlyPrime: number;
  splitRate: number;
  commissionRate: number;
  split: PrimeSplitRoles;
  errorCode?: PrimeLineErrorCode;
};

export const MISSION_PRIME_ERROR_CRA_STATUS = 'Erreur de chargement';
/** Prime mensuelle mission freelance — voir `computeMissionPrime` (`api.talents`). */
export type MissionPrime = BasePrime<FreelanceJobType> & {
  craStatus: CRAStatus | typeof MISSION_PRIME_ERROR_CRA_STATUS;
  normalWorkedDays: number;
  saturdaysWorkedDays: number;
  sundayAndholidaysWorkedDays: number;
  astreinteWorkedDays: number;
};

/** Prime mensuelle facturation CDI — voir `computeBillingPrime` (`api.talents`). */
export type BillingPrime = BasePrime<CdiJobType> & {
  paymentTerms?: PaymentTerms;
};

/** Statut d’une régularisation TAM — voir `upsertRegularisationByAdmin` (`api.talents`, TEC-2789). */
export type PrimeRegularisationStatus = 'pending' | 'validated';

/** Régularisation mensuelle TAM persistée — voir `upsertRegularisationByAdmin` (`api.talents`, TEC-2789). */
export type PrimeRegularisation = {
  id: string;
  tam: string;
  monthYear: string;
  total: number;
  status: PrimeRegularisationStatus;
  createdBy: string;
  modifiedBy: string | null;
  createdAt: Date;
  modifiedAt: Date | null;
};

/** Agrégat consultation mensuelle TAM — voir `computeMonthlyTamPrime` (`api.talents`). */
export type MonthlyTamPrime = {
  primes: (MissionPrime | BillingPrime)[];
  bonus: number;
  regularisation: number;
  totalFreelanceMarge: number;
  totalBillingMarge: number;
  totalMarge: number;
  totalPrime: number;
  totalDeals: number;
  totalMissions: number;
};

/** Prime mensuelle validée et figée — voir `getTamPrimes` (`api.talents`, TEC-2811). */
export type ValidatedTamPrime = MonthlyTamPrime & {
  id: string;
  monthYear: string;
  validationDate: Date;
};

/** Agrégat annuel TAM — voir `getTamPrimes` (`api.talents`, TEC-2811). */
export type AnnualTamPrime = {
  tam: string;
  tamSeniority: BloomieTamSeniority;
  goalCompletion: number;
  validatedPrimes: ValidatedTamPrime[];
  pendingPrimes: MonthlyTamPrime | null;
};

export const TAM_ANNUAL_MARGIN_OBJECTIVES: Record<Exclude<BloomieTamSeniority, 'Director'>, number> = {
  Apprenti: 109_000,
  Associate: 210_000,
  Confirme: 330_000,
  Senior: 438_000,
  Principal: 558_000,
  Executive: 666_000,
};
